// Event names and property shapes for query permit telemetry.

// event names
export const QUERY_PERMIT_SOFT_THROTTLE_EVENT = `query_permit.soft_throttle`;
export const QUERY_PERMIT_POOL_STATS_EVENT = `query_permit.pool_stats`;

// properties
export enum QueryPermitClientType {
  SharedWorker = `SHARED_WORKER`,
  Local = `LOCAL`,
}

// Fields common to every query-permit event: the project the metric is scoped to and the client
// that emitted it.
export interface QueryPermitEventBase {
  project_id: number;
  client_type: QueryPermitClientType;
}

export interface SoftThrottleEventProps extends QueryPermitEventBase {
  // Number of queries that were throttled
  engaged_count: number;
  // Histogram of engaged grants by backoff depth level
  backoff_level_1: number;
  backoff_level_2: number;
  backoff_level_3: number;
  backoff_level_4: number;
  backoff_level_5_plus: number;
  // Query rate over the flush window
  sustained_rpm: number;
}

// One aggregate per project per flush window covering the semaphore's load behavior: throughput,
// queue/saturation, wait, involuntary reclaim, and busy-period drain. Counts and sums are raw so
// windows re-aggregate correctly (sum/sum is a true average across windows; averaging per-window
// averages is not). Kept separate from soft-throttle, which has its own event.
export interface PoolStatsEventProps extends QueryPermitEventBase {
  // Slot-gated permits granted in the window — those that went through the queue and held a pool
  // slot. Shared denominator for wait-avg and reclaim-rate, so it measures queue-drain performance.
  // Excludes queue_full_grants (which bypass the queue and hold no slot); sum both for total grants
  // delivered to tabs.
  grant_count: number;
  // Of grant_count, those on the background (prefetch) pool tier — rough sense of how much of the
  // work is user-facing vs prefetch. Foreground = grant_count - background_pool_grant_count.
  background_pool_grant_count: number;
  // Of grant_count, those from a background (non-viewed) tab — rough sense of cross-tab load.
  // Foreground-tab = grant_count - background_tab_grant_count.
  background_tab_grant_count: number;

  // Queue / saturation
  // Pool-state samples taken in the window
  sample_count: number;
  // Peak waiting-queue depth observed in the window (max getPendingCount)
  pending_peak: number;
  // Samples where in-flight permits reached the cap (saturated_samples / sample_count = fraction)
  saturated_samples: number;
  // Acquires granted immediately without joining the queue because it was already full — the worker
  // was holding QUERY_PERMIT_MAX_PENDING queued acquires across all pools (the fail-open circuit
  // breaker). These draw no pool slot, so a nonzero value means the pending ceiling was hit and the
  // pool stopped limiting concurrency for those requests; the backend + browser connection pool
  // became the limiter. Pairs with pending_peak: pending_peak pinned at the ceiling alongside a
  // rising queue_full_grants is the unambiguous "we overflowed" signal.
  queue_full_grants: number;

  // Wait for a concurrency slot (enqueue->grant). Throttle-agnostic: this is time spent queued
  // for a permit only. Soft-throttle backoff is applied *after* the slot is granted and is NOT
  // included here (it has its own signal), so this isolates concurrency-limiting latency from
  // rate-limiting latency.
  // Longest wait-for-grant in the window, in ms
  wait_for_grant_ms_max: number;
  // Sum of waits-for-grant in the window (avg = wait_for_grant_ms_total / grant_count)
  wait_for_grant_ms_total: number;

  // Reclaim / leak
  // Involuntary reclaims of slot-holding permits (max-hold + port-dropped) in the window, so it
  // measures leaked concurrency. Queue-full grants hold no slot and aren't tracked by the worker,
  // so they never appear here (see queue_full_grants). Worker-only; 0 for Local.
  reclaimed_total: number;

  // Drain
  // Busy periods (queue non-empty -> empty) that completed in the window
  drain_count: number;
  // Longest busy-period duration in the window, in ms
  drain_ms_max: number;
  // Sum of busy-period durations in the window (avg = drain_ms_total / drain_count)
  drain_ms_total: number;
}

export type SoftThrottleTrackEvent = {
  name: typeof QUERY_PERMIT_SOFT_THROTTLE_EVENT;
  properties: SoftThrottleEventProps;
};
export type PoolStatsTrackEvent = {
  name: typeof QUERY_PERMIT_POOL_STATS_EVENT;
  properties: PoolStatsEventProps;
};

export type QueryPermitTrackEvent = SoftThrottleTrackEvent | PoolStatsTrackEvent;
